import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Popover, TextInput, useSafeMantineTheme } from '@mantine/core';
import { concat } from 'shared/collection';
import { doSafe, doTo } from 'shared/promise';
import { Fmt, ForecastBaselineAgg, ForecastLikelihood, ForecastQuestionAgg, Forecast as F, isLikelihoodValid } from 'shared/forecast';
import { parseNum } from 'shared/format';
import { notNull } from 'shared/type';
import { ChildrenProps } from 'shared/ui/uiType';
import { ResultColorFn } from 'shared/forecast/results/QuestionResults';
import { Slider } from 'components/form/Slider';
import { MarkCfg } from 'components/form/Slider/Marks';
import { ReactNode, useEffect, useState } from 'react';
import { ForecastMarkLabel, ForecastMark, ForecastMarkLine } from './ForecastCommon';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { match, P } from 'ts-pattern';
import { QRComparison, QRComparisons } from 'shared/forecast/results/QRComparisons';
import { binForecast, BinForecast, binForecastScale, ForecastBinBar } from 'shared/chart';
import { scaleLinear } from 'd3-scale';
import { ForecastComponentResolution } from './Forecast';
import { omit, round } from 'remeda';
import { useElementSize } from 'shared/ui';
const format = {
  precise: {
    fmt: '#[.][####]',
    unit: '%'
  },
  points: {
    fmt: '#[.][####]',
    unit: 'pp.',
    showSign: true
  }
} satisfies Record<string, Fmt.Option>;
type LikelihoodEditValue = Pick<ForecastLikelihood, 'likelihood' | 'relativePoints'>;
const NudgeArrow = t0 => {
  const $ = _c(11);
  const {
    type,
    usePoints,
    relativePoints,
    likelihood,
    onChange
  } = t0;
  const existingValue = usePoints ? relativePoints : likelihood;
  if (!existingValue) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <></>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== existingValue || $[2] !== onChange || $[3] !== type || $[4] !== usePoints) {
    t1 = () => {
      const value = existingValue + (type == "left" ? -0.1 : 0.1);
      onChange(value, {
        type: usePoints ? "points" : "likelihood"
      });
    };
    $[1] = existingValue;
    $[2] = onChange;
    $[3] = type;
    $[4] = usePoints;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== type) {
    t2 = type == "left" ? <HiChevronLeft size={20} /> : <HiChevronRight size={20} />;
    $[6] = type;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <div className="group-hover/nudge:opacity-100 opacity-0 flex justify-center items-center"><ActionIcon size="sm" onClick={t1}>{t2}</ActionIcon></div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
export const ForecastLikelihoodEdit = t0 => {
  const $ = _c(28);
  const {
    value,
    onChange: _onChange,
    aggForecast,
    myForecast,
    showGroup,
    questionAgg,
    baselineAgg,
    color,
    menuItems,
    comparisons,
    resolution,
    children
  } = t0;
  let t1;
  if ($[0] !== baselineAgg) {
    t1 = points => points != null && baselineAgg?.you != null ? points + baselineAgg.you : undefined;
    $[0] = baselineAgg;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const likelihoodFromPoints = t1;
  let t2;
  if ($[2] !== baselineAgg) {
    t2 = likelihood => likelihood != null && baselineAgg?.you != null ? likelihood - baselineAgg.you : undefined;
    $[2] = baselineAgg;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const pointsFromLikelihood = t2;
  const usePoints = baselineAgg != null && baselineAgg.you != null && baselineAgg.youWith == null;
  let t3;
  if ($[4] !== likelihoodFromPoints || $[5] !== pointsFromLikelihood || $[6] !== usePoints) {
    t3 = t4 => {
      const {
        likelihood: likelihood_0,
        relativePoints
      } = t4;
      return {
        likelihood: usePoints ? F.clampLikelihood(likelihoodFromPoints(relativePoints)) ?? likelihood_0 : likelihood_0,
        relativePoints: usePoints ? relativePoints ?? pointsFromLikelihood(likelihood_0) : undefined
      };
    };
    $[4] = likelihoodFromPoints;
    $[5] = pointsFromLikelihood;
    $[6] = usePoints;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const current = doTo({
    ...myForecast,
    ...value
  }, t3);
  const currentBaseline = baselineAgg && baselineAgg.youWith && myForecast && myForecast.code && current.likelihood != null ? baselineAgg.youWith({
    [myForecast.code]: current.likelihood
  }) : baselineAgg?.you;
  const {
    likelihood: likelihood_1,
    relativePoints: relativePoints_0
  } = current;
  let t4;
  if ($[8] !== likelihoodFromPoints || $[9] !== pointsFromLikelihood || $[10] !== usePoints) {
    t4 = t5 => {
      const {
        likelihood: likelihood_2,
        relativePoints: relativePoints_1
      } = t5;
      return {
        likelihood: usePoints ? F.clampLikelihood(likelihoodFromPoints(relativePoints_1)) ?? likelihood_2 : likelihood_2,
        relativePoints: relativePoints_1 ?? pointsFromLikelihood(likelihood_2)
      };
    };
    $[8] = likelihoodFromPoints;
    $[9] = pointsFromLikelihood;
    $[10] = usePoints;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const previous = doSafe(myForecast, t4);
  const [text, setText] = useState((usePoints ? relativePoints_0 : likelihood_1)?.toString() ?? "");
  useEffect(() => {
    if (parseNum(text) != likelihood_1) {
      setText(Fmt.fmt(usePoints ? relativePoints_0 : likelihood_1, {
        fmt: "#[.][####]"
      }) ?? "");
    }
  }, [likelihood_1, usePoints]);
  let t5;
  if ($[12] !== _onChange || $[13] !== likelihoodFromPoints || $[14] !== pointsFromLikelihood || $[15] !== usePoints) {
    t5 = (vRaw, t6) => {
      const {
        type,
        mode
      } = t6;
      const v = round(vRaw, 1);
      const likelihood_3 = type == "likelihood" ? F.clampLikelihood(v, mode) : likelihoodFromPoints(v);
      const relativePoints_2 = type == "points" ? v : usePoints ? pointsFromLikelihood(v) : undefined;
      return _onChange?.({
        likelihood: likelihood_3,
        relativePoints: relativePoints_2
      });
    };
    $[12] = _onChange;
    $[13] = likelihoodFromPoints;
    $[14] = pointsFromLikelihood;
    $[15] = usePoints;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const onChange = t5;
  const showForecasts = aggForecast != null && (showGroup ?? myForecast != null);
  let t6;
  if ($[17] !== usePoints) {
    t6 = s => doTo(parseNum(s), n => usePoints ? n == null ? "not a number" : null : match(n).with(P.nullish, _temp).with(P.number.lte(0), _temp2).with(P.number.gte(100), _temp3).otherwise(_temp4));
    $[17] = usePoints;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const textError = t6;
  const {
    ref,
    width
  } = useElementSize();
  const scale = scaleLinear([0, 100], [24, width - 24]).nice();
  const bins = width ? binForecast({
    forecasts: resolution.forecasts?.map(f => match(f.cfg).when(isLikelihoodValid, t7 => {
      const {
        likelihood: likelihood_4,
        notes,
        relativePoints: relativePoints_3
      } = t7;
      return {
        ...omit(f, ["cfg"]),
        value: likelihood_4,
        valueRelative: usePoints ? relativePoints_3 : undefined,
        notes: notes ?? ""
      } as BinForecast;
    }).otherwise(_temp5)).filter(notNull) ?? [],
    domain: scale.domain() as [number, number]
  }).bins.filter(_temp6) : undefined;
  const {
    colors
  } = useSafeMantineTheme();
  let t8;
  if ($[19] !== menuItems) {
    t8 = menuItems && <div className="flex gap-2 justify-end">{menuItems}</div>;
    $[19] = menuItems;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = likelihood_1 ?? 50;
  let t10;
  if ($[21] !== t9) {
    t10 = {
      likelihood: t9
    };
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== onChange) {
    t11 = t12 => {
      const {
        value: value_1
      } = t12;
      onChange(value_1, {
        type: "likelihood"
      });
    };
    $[23] = onChange;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== comparisons || $[26] !== showGroup) {
    t12 = comparisons && showGroup && <QRComparisons className="mx-8" comparisons={comparisons} scale="auto-likelihood" fmt={format.precise} />;
    $[25] = comparisons;
    $[26] = showGroup;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return <div ref={ref}>{t8}{children}<Slider className={`!mx-8 ${showForecasts ? "my-16" : `mt-4 ${likelihood_1 != null ? "mb-16" : "mb-8"}`}`} showLabelOnHover={false} thumbs={{
      likelihood: {
        showLabel: false,
        thumbSize: 25,
        className: `!shadow-lg ${likelihood_1 == null ? "opacity-30" : "opacity-80"}`,
        children: v_0 => {
          const nudgeProps = {
            usePoints,
            relativePoints: relativePoints_0,
            likelihood: likelihood_1,
            onChange
          };
          return <Popover width={160} position="top" withArrow={true} shadow="md" withinPortal={true}><Popover.Target><div className="group/nudge h-full w-full flex justify-center items-center" data-test-id="thumb-likelihood"><div className="flex items-center"><NudgeArrow {...nudgeProps} type="left" /><div className="w-8 pointer-events-none" /><NudgeArrow {...nudgeProps} type="right" /></div></div></Popover.Target><Popover.Dropdown className="!p-2 !opacity-100" onMouseDownCapture={_temp7}><div className="flex items-center gap-2"><TextInput value={text} onKeyDownCapture={_temp8} onChange={e_1 => {
                  const t = e_1.target.value;
                  setText(t);
                  const value_0 = parseNum(t);
                  const error = textError(t);
                  if (error) {
                    return;
                  }
                  const existingValue = usePoints ? relativePoints_0 : likelihood_1;
                  if (value_0 != null && value_0 != existingValue) {
                    onChange(value_0, {
                      type: usePoints ? "points" : "likelihood",
                      mode: usePoints ? "auto" : "manual"
                    });
                  }
                }} error={textError(text)} rightSectionWidth={1} data-test-id="likelihood-input" /><b>{usePoints ? "pp." : "%"}</b></div></Popover.Dropdown></Popover>;
        }
      }
    }} value={t10} marks={concat({
      value: 0,
      label: "0%"
    }, {
      value: 50,
      label: "50%"
    }, {
      value: 100,
      label: "100%"
    }, showForecasts ? {
      location: "top",
      value: aggForecast,
      label: <ForecastMarkLabel value={aggForecast} color={color("group")} label="group" location="top" />,
      marker: <ForecastMark color={color("group")} />
    } as MarkCfg : undefined, questionAgg?.group != null ? {
      value: questionAgg.group,
      location: "top",
      marker: <ForecastMarkLine color={color("question")} label={`group baseline ${Fmt.fmt(questionAgg.group)}`} location="top" />
    } : undefined, showGroup && baselineAgg?.group != null ? {
      value: baselineAgg?.group,
      location: "top",
      marker: <ForecastMarkLine color={color("group")} location="top" label={`implied group baseline: ${Fmt.fmt(baselineAgg?.group)}`} />
    } : undefined, currentBaseline != null ? {
      value: currentBaseline,
      location: "bottom",
      marker: <ForecastMarkLine color={color("question")} location="bottom" label={`your baseline ${Fmt.fmt(currentBaseline)}`} />
    } : undefined, previous == null || (usePoints ? previous.relativePoints == null || previous.relativePoints == relativePoints_0 : previous.likelihood == null || previous.likelihood == likelihood_1) ? undefined : {
      location: "bottom",
      value: previous.likelihood,
      label: <ForecastMarkLabel value={usePoints ? Fmt.fmt(previous.relativePoints, format.points) : Fmt.fmt(previous.likelihood, format.precise)} color={color("default")} label="my saved" className="opacity-70" location="bottom" />,
      marker: <ForecastMark color={color("default")} />
    } as MarkCfg, likelihood_1 != null ? {
      location: "bottom",
      value: likelihood_1,
      label: <ForecastMarkLabel value={usePoints ? `${Fmt.fmt(relativePoints_0, format.points)} / ${Fmt.fmt(likelihood_1, format.precise)}` : Fmt.fmt(likelihood_1, format.precise)} color={color("default")} label="my forecast" location="bottom" />,
      marker: <></>
    } as MarkCfg : undefined)} onChange={t11} />{showForecasts && <div>{bins && <ForecastBinBar {...{
        bins,
        scale
      }} scaleBinColor={binForecastScale({
        bins,
        color: color("group"),
        colors
      })} showValue={false} format={Fmt.getOptions({
        forecastType: "likelihood"
      })} />}</div>}{t12}</div>;
};
function _temp(_) {
  return "Not a number";
}
function _temp2(__0) {
  return "must be greater than 0";
}
function _temp3(__1) {
  return "must be less than 100";
}
function _temp4() {
  return null;
}
function _temp5() {}
function _temp6(b) {
  return b.forecasts.length > 0;
}
function _temp7(e) {
  return e.stopPropagation();
}
function _temp8(e_0) {
  e_0.stopPropagation();
}